// Árvore da central de ajuda, montada no front a partir da lista plana de GET /pages/list (a API não devolve a árvore).
// Funções puras, sem chamada à API: rodam no navegador (hooks) e no servidor (rota /p/[slug]).

/** O mínimo que a árvore e a trilha precisam de cada página. */
interface IPageTreeItem {
    id: string
    slug: string
    parentId: string | null
}

export type PageTreeNode<T extends IPageTreeItem> = T & { children: PageTreeNode<T>[] }

/**
 * Monta a árvore de páginas a partir da lista plana.
 * Raízes = itens com parentId nulo (ou cujo pai não está na lista).
 */
export function buildPageTree<T extends IPageTreeItem>(list: T[]): PageTreeNode<T>[] {
    const byId = new Map<string, PageTreeNode<T>>()
    list.forEach((p) => {
        byId.set(p.id, { ...p, children: [] })
    })

    const roots: PageTreeNode<T>[] = []
    byId.forEach((node) => {
        if (node.parentId && byId.has(node.parentId)) {
            byId.get(node.parentId)!.children.push(node)
        } else {
            roots.push(node)
        }
    })

    return roots
}

/**
 * Cadeia de breadcrumb (da raiz até a página do slug informado).
 * Retorna [] quando o slug não existe na lista.
 */
export function findBreadcrumb<T extends IPageTreeItem>(list: T[], slug: string): T[] {
    const bySlug = new Map(list.map((p) => [p.slug, p]))
    const byId = new Map(list.map((p) => [p.id, p]))

    const current = bySlug.get(slug)
    if (!current) return []

    const trail: T[] = [current]
    let parentId = current.parentId
    while (parentId && byId.has(parentId)) {
        const parent = byId.get(parentId)!
        trail.unshift(parent)
        parentId = parent.parentId
    }

    return trail
}
